import { useState } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Clock, Calendar as CalendarIcon, List, Package, GripVertical } from "lucide-react";
import { format } from "date-fns";
import { tr } from "date-fns/locale/tr";
import { useTranslation } from 'react-i18next';

// Helper functions
const getPriorityColor = (priority) => {
  switch (priority) {
    case "high":
      return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
    case "medium":
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400";
    case "low":
      return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
    default:
      return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300";
  }
};

const getPriorityLabel = (priority) => {
  switch (priority) {
    case "high":
      return "Yüksek";
    case "medium":
      return "Orta";
    case "low":
      return "Düşük";
    default:
      return priority;
  }
};

const formatMinutes = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0) {
    return `${hours}s ${mins}dk`;
  }
  return `${mins}dk`;
};

const getInitials = (name) => {
  if (!name) return "?";
  return name
    .split(" ")
    .map(n => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

// Sortable Task Card
function SortableTaskCard({ task, users, onTaskClick, isDone }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getUserById = (userId) => {
    return users.find(u => u.id === userId);
  };

  const getTaskDisplayDate = (task) => {
    if (task.status === 'done' && task.actual_end_date) {
      return {
        endDate: task.actual_end_date,
        isActual: true
      };
    }
    if (task.estimated_end_date) {
      return {
        endDate: task.estimated_end_date,
        isActual: false
      };
    }
    if (task.due_date) {
      return {
        endDate: task.due_date,
        isActual: false
      };
    }
    return null;
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Card
        className={`cursor-pointer hover:shadow-md transition-shadow bg-white dark:bg-gray-800 ${isDone ? 'opacity-80' : ''} ${isDragging ? 'ring-2 ring-blue-500' : ''}`}
        data-testid={`task-card-${task.id}`}
      >
        <CardHeader className="pb-3">
          <div className="flex justify-between items-start gap-2">
            <div className="flex items-center gap-2 flex-1">
              <button
                {...attributes}
                {...listeners}
                className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded touch-none"
                aria-label="Drag task"
              >
                <GripVertical className="h-4 w-4 text-gray-400" />
              </button>
              <CardTitle
                className={`text-base font-medium flex-1 ${isDone ? 'line-through' : ''}`}
                onClick={() => onTaskClick(task)}
              >
                {task.title}
              </CardTitle>
            </div>
            <Badge className={getPriorityColor(task.priority)} data-testid={`task-priority-${task.id}`}>
              {getPriorityLabel(task.priority)}
            </Badge>
          </div>
          {task.description && (
            <CardDescription
              className="line-clamp-2 text-sm cursor-pointer"
              onClick={() => onTaskClick(task)}
            >
              {task.description}
            </CardDescription>
          )}
        </CardHeader>
        <CardContent className="pb-3 space-y-2" onClick={() => onTaskClick(task)}>
          {/* Alt görev, Kaynak, Maliyet */}
          <div className="flex items-center gap-3 text-xs text-gray-600 dark:text-gray-400 flex-wrap">
            {task.subtask_count > 0 && (
              <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                <List className="h-3 w-3" />
                <span>{task.subtask_count} alt görev</span>
              </div>
            )}
            {task.has_resources && (
              <div className="flex items-center gap-1 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded text-blue-700 dark:text-blue-400">
                <Package className="h-3 w-3" />
                <span>Kaynak var</span>
              </div>
            )}
            {task.estimated_cost && (
              <div className="flex items-center gap-1 bg-green-50 dark:bg-green-900/30 px-2 py-0.5 rounded text-green-700 dark:text-green-400 font-medium">
                <span>₺{task.estimated_cost.toLocaleString('tr-TR')}</span>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between gap-4 text-xs text-gray-500 dark:text-gray-400">
            <div className="flex items-center gap-4">
              {task.time_spent > 0 && (
                <div className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {formatMinutes(task.time_spent)}
                </div>
              )}
              {(() => {
                const displayDate = getTaskDisplayDate(task);
                if (displayDate) {
                  return (
                    <div className="flex items-center gap-1">
                      <CalendarIcon className="h-3 w-3" />
                      <span className={displayDate.isActual ? 'font-medium text-green-700 dark:text-green-400' : ''}>
                        {format(new Date(displayDate.endDate), "d MMM", { locale: tr })}
                      </span>
                      {displayDate.isActual && <span className="text-[10px] text-green-600 dark:text-green-400">✓</span>}
                    </div>
                  );
                }
                return null;
              })()}
            </div>
            {task.assignee_id && getUserById(task.assignee_id) && (
              <Avatar className="h-6 w-6">
                <AvatarFallback className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                  {getInitials(getUserById(task.assignee_id).name)}
                </AvatarFallback>
              </Avatar>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Droppable Column
function DroppableColumn({ id, title, tasks, users, onTaskClick, color }) {
  const { t } = useTranslation();
  const { setNodeRef, isOver } = useDroppable({ id });
  const isDone = id === 'done';

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border dark:border-gray-700">
        <h3 className="font-semibold text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
          <div className={`w-3 h-3 rounded-full ${color}`}></div>
          {title}
          <Badge variant="secondary" className="ml-auto">{tasks.length}</Badge>
        </h3>
      </div>
      <div
        ref={setNodeRef}
        className={`space-y-3 min-h-[200px] p-2 rounded-lg transition-colors ${
          isOver ? 'bg-blue-50 dark:bg-blue-900/20 border-2 border-dashed border-blue-300 dark:border-blue-700' : ''
        }`}
        data-testid={`${id}-column`}
      >
        <SortableContext items={tasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
          {tasks.map((task) => (
            <SortableTaskCard
              key={task.id}
              task={task}
              users={users}
              onTaskClick={onTaskClick}
              isDone={isDone}
            />
          ))}
        </SortableContext>
      </div>
    </div>
  );
}

// Drag Overlay Task Card (for visual feedback during drag)
function DragOverlayCard({ task, users }) {
  const getUserById = (userId) => {
    return users.find(u => u.id === userId);
  };

  return (
    <Card className="cursor-grabbing shadow-xl ring-2 ring-blue-500 bg-white dark:bg-gray-800 rotate-3">
      <CardHeader className="pb-3">
        <div className="flex justify-between items-start gap-2">
          <CardTitle className="text-base font-medium">{task.title}</CardTitle>
          <Badge className={getPriorityColor(task.priority)}>
            {getPriorityLabel(task.priority)}
          </Badge>
        </div>
        {task.description && (
          <CardDescription className="line-clamp-2 text-sm">
            {task.description}
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="pb-3">
        <div className="flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center gap-4">
            {task.time_spent > 0 && (
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {formatMinutes(task.time_spent)}
              </div>
            )}
          </div>
          {task.assignee_id && getUserById(task.assignee_id) && (
            <Avatar className="h-6 w-6">
              <AvatarFallback className="text-xs bg-blue-100 text-blue-700">
                {getInitials(getUserById(task.assignee_id).name)}
              </AvatarFallback>
            </Avatar>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function KanbanBoard({ tasks, users, onTaskClick, onStatusChange }) {
  const { t } = useTranslation();
  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const todoTasks = tasks.filter((t) => t.status === "todo");
  const inProgressTasks = tasks.filter((t) => t.status === "in-progress");
  const doneTasks = tasks.filter((t) => t.status === "done");

  const activeTask = activeId ? tasks.find(t => t.id === activeId) : null;

  const findContainer = (id) => {
    if (id === 'todo' || id === 'in-progress' || id === 'done') {
      return id;
    }

    const task = tasks.find(t => t.id === id);
    return task?.status;
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragOver = (event) => {
    const { active, over } = event;

    if (!over) return;

    const activeContainer = findContainer(active.id);
    const overContainer = findContainer(over.id);

    if (!activeContainer || !overContainer || activeContainer === overContainer) {
      return;
    }

    // Status will be updated on drag end
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    setActiveId(null);

    if (!over) return;

    const activeContainer = findContainer(active.id);
    let overContainer = findContainer(over.id);

    // If dropped on a column directly
    if (['todo', 'in-progress', 'done'].includes(over.id)) {
      overContainer = over.id;
    }

    if (!activeContainer || !overContainer) return;

    // If moved to different column, update status
    if (activeContainer !== overContainer) {
      onStatusChange(active.id, overContainer);
    }
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="kanban-board">
        <DroppableColumn
          id="todo"
          title={t('todo')}
          tasks={todoTasks}
          users={users}
          onTaskClick={onTaskClick}
          color="bg-gray-400"
        />
        <DroppableColumn
          id="in-progress"
          title={t('inProgress')}
          tasks={inProgressTasks}
          users={users}
          onTaskClick={onTaskClick}
          color="bg-blue-500"
        />
        <DroppableColumn
          id="done"
          title={t('done')}
          tasks={doneTasks}
          users={users}
          onTaskClick={onTaskClick}
          color="bg-green-500"
        />
      </div>
      <DragOverlay>
        {activeTask ? (
          <DragOverlayCard task={activeTask} users={users} />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
